import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ModuleLayout from '../components/ModuleLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Loader2, Zap, Image, DollarSign, TrendingUp,
  AlertTriangle, Crown, ArrowUpRight, Check, X as XIcon,
  MessageSquare, Languages, FileText, Palette, Megaphone, BarChart3, Bot,
  Bell, Mail, Clock, Settings2
} from 'lucide-react';
import { toast } from 'sonner';
import { Switch } from '../components/ui/switch';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend
} from 'recharts';

const FEATURE_ICONS = {
  chat: MessageSquare, translation: Languages, description: FileText,
  image_generation: Palette, marketing: Megaphone,
  analytics_narration: BarChart3, order_agent: Bot,
};

const PLAN_COLORS = {
  trial: { bg: 'bg-slate-500/10', text: 'text-slate-600', border: 'border-slate-300', accent: '#6B7280' },
  basic: { bg: 'bg-blue-500/10', text: 'text-blue-600', border: 'border-blue-300', accent: '#2563EB' },
  pro: { bg: 'bg-amber-500/10', text: 'text-amber-600', border: 'border-amber-300', accent: '#D97706' },
  enterprise: { bg: 'bg-purple-500/10', text: 'text-purple-600', border: 'border-purple-300', accent: '#7C3AED' },
};

const ALL_FEATURES = ['chat', 'translation', 'description', 'image_generation', 'marketing', 'analytics_narration', 'order_agent'];

export default function AIUsagePage() {
  const { authHeaders, API } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [usage, setUsage] = useState(null);
  const [history, setHistory] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [alertsEnabled, setAlertsEnabled] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [usageRes, historyRes, alertsRes] = await Promise.all([
        axios.get(`${API}/ai/usage/me`, authHeaders()),
        axios.get(`${API}/ai/usage/history?months=6`, authHeaders()),
        axios.get(`${API}/ai/usage/alerts`, authHeaders()),
      ]);
      setUsage(usageRes.data);
      setHistory(historyRes.data.history || []);
      setAlerts(alertsRes.data.alerts || []);
      setAlertsEnabled(usageRes.data.alert_settings?.enabled ?? true);
    } catch {
      toast.error(t('common.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const toggleAlerts = async (enabled) => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/ai/usage/alerts/settings`, { enabled }, authHeaders());
      setAlertsEnabled(enabled);
      toast.success(t('aiUsage.settingsSaved'));
    } catch {
      toast.error(t('common.saveFailed'));
    } finally {
      setSavingSettings(false);
    }
  };

  if (loading) return (
    <ModuleLayout moduleKey="ai">
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    </ModuleLayout>
  );

  const plan = usage?.plan || 'trial';
  const planStyle = PLAN_COLORS[plan] || PLAN_COLORS.trial;
  const isUnlimited = usage?.is_unlimited;
  const textUsed = usage?.usage?.text_requests || 0;
  const imageUsed = usage?.usage?.image_requests || 0;
  const textLimit = usage?.limits?.text_requests || 0;
  const imageLimit = usage?.limits?.image_requests || 0;
  const textPct = usage?.percentages?.text || 0;
  const imagePct = usage?.percentages?.image || 0;
  const totalCost = usage?.usage?.total_cost_usd || 0;
  const availableFeatures = usage?.limits?.features || [];

  const chartData = [...history].reverse().map(h => ({
    month: h.month,
    [t('aiUsage.text')]: h.text_requests,
    [t('aiUsage.image')]: h.image_requests,
  }));

  const getStatusColor = (pct) => {
    if (pct >= 90) return 'text-red-500';
    if (pct >= 70) return 'text-amber-500';
    return 'text-emerald-500';
  };

  const getProgressColor = (pct) => {
    if (pct >= 90) return '[&>div]:bg-red-500';
    if (pct >= 70) return '[&>div]:bg-amber-500';
    return '[&>div]:bg-emerald-500';
  };

  return (
    <ModuleLayout moduleKey="ai">
      <div className="space-y-6 max-w-5xl mx-auto" data-testid="ai-usage-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-bold text-2xl text-foreground flex items-center gap-2" style={{ fontFamily: 'Tajawal, sans-serif' }}>
              <Zap className="w-6 h-6 text-primary" />
              {t('aiUsage.title')}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">{t('aiUsage.subtitle')}</p>
          </div>
          <Badge className={`${planStyle.bg} ${planStyle.text} border-0 text-sm px-3 py-1`} data-testid="current-plan-badge">
            <Crown className="w-3.5 h-3.5 me-1.5" />
            {t('aiUsage.currentPlan')}: {plan.toUpperCase()}
          </Badge>
        </div>

        {/* Quota Warning */}
        {(textPct >= 80 || imagePct >= 80) && !isUnlimited && (
          <Card className="border-amber-500/30 bg-amber-50 dark:bg-amber-950/20" data-testid="quota-warning">
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                <span className="text-sm font-medium text-amber-700 dark:text-amber-400">
                  {textPct >= 100 || imagePct >= 100
                    ? t('aiUsage.quotaExceeded')
                    : t('aiUsage.quotaWarning')}
                </span>
              </div>
              <Button
                size="sm"
                onClick={() => navigate('/dashboard/settings/subscription')}
                className="bg-amber-500 hover:bg-amber-600 text-white"
                data-testid="upgrade-btn"
              >
                <ArrowUpRight className="w-3.5 h-3.5 me-1" />
                {t('aiUsage.upgradeNow')}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Usage Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Text Requests Card */}
          <Card className="overflow-hidden" data-testid="text-usage-card">
            <div className="h-1 bg-gradient-to-r from-blue-500 to-indigo-600" />
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-blue-600" />
                  </div>
                  <span className="text-sm font-semibold text-foreground">{t('aiUsage.textRequests')}</span>
                </div>
                <span className={`text-xs font-bold ${getStatusColor(textPct)}`}>
                  {isUnlimited ? t('aiUsage.unlimited') : `${textPct}%`}
                </span>
              </div>
              <div>
                <div className="flex items-baseline gap-1 mb-2">
                  <span className="text-3xl font-extrabold text-foreground">{textUsed}</span>
                  {!isUnlimited && (
                    <span className="text-sm text-muted-foreground">
                      {t('aiUsage.usedOf')} {textLimit}
                    </span>
                  )}
                </div>
                {!isUnlimited && (
                  <Progress value={Math.min(textPct, 100)} className={`h-2 ${getProgressColor(textPct)}`} />
                )}
              </div>
            </CardContent>
          </Card>

          {/* Image Requests Card */}
          <Card className="overflow-hidden" data-testid="image-usage-card">
            <div className="h-1 bg-gradient-to-r from-purple-500 to-pink-600" />
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center">
                    <Image className="w-5 h-5 text-purple-600" />
                  </div>
                  <span className="text-sm font-semibold text-foreground">{t('aiUsage.imageRequests')}</span>
                </div>
                <span className={`text-xs font-bold ${getStatusColor(imagePct)}`}>
                  {isUnlimited ? t('aiUsage.unlimited') : `${imagePct}%`}
                </span>
              </div>
              <div>
                <div className="flex items-baseline gap-1 mb-2">
                  <span className="text-3xl font-extrabold text-foreground">{imageUsed}</span>
                  {!isUnlimited && (
                    <span className="text-sm text-muted-foreground">
                      {t('aiUsage.usedOf')} {imageLimit}
                    </span>
                  )}
                </div>
                {!isUnlimited && (
                  <Progress value={Math.min(imagePct, 100)} className={`h-2 ${getProgressColor(imagePct)}`} />
                )}
              </div>
            </CardContent>
          </Card>

          {/* Cost Card */}
          <Card className="overflow-hidden" data-testid="cost-card">
            <div className="h-1 bg-gradient-to-r from-emerald-500 to-teal-600" />
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center gap-2.5">
                <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-emerald-600" />
                </div>
                <span className="text-sm font-semibold text-foreground">{t('aiUsage.totalCost')}</span>
              </div>
              <div>
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-extrabold text-foreground">${totalCost.toFixed(3)}</span>
                  <span className="text-sm text-muted-foreground">USD</span>
                </div>
                <p className="text-xs text-muted-foreground mt-2">{usage?.month}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Available Features */}
        <Card data-testid="features-card">
          <CardContent className="p-5">
            <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
              <Zap className="w-4 h-4 text-primary" />
              {t('aiUsage.features')}
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {ALL_FEATURES.map(feature => {
                const Icon = FEATURE_ICONS[feature] || Zap;
                const isAvailable = availableFeatures.includes(feature);
                return (
                  <div
                    key={feature}
                    className={`flex items-center gap-2.5 p-3 rounded-xl border transition-all ${
                      isAvailable
                        ? 'border-emerald-200 bg-emerald-50/50 dark:bg-emerald-950/20 dark:border-emerald-800'
                        : 'border-border/50 bg-muted/30 opacity-60'
                    }`}
                    data-testid={`feature-${feature}`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      isAvailable ? 'bg-emerald-500/10' : 'bg-muted'
                    }`}>
                      <Icon className={`w-4 h-4 ${isAvailable ? 'text-emerald-600' : 'text-muted-foreground'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium truncate">{t(`aiUsage.${feature}`)}</p>
                    </div>
                    {isAvailable
                      ? <Check className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                      : <XIcon className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                    }
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Usage History Chart */}
        {chartData.length > 0 && (
          <Card data-testid="usage-chart">
            <CardContent className="p-5">
              <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-primary" />
                {t('aiUsage.usageHistory')}
              </h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip
                      contentStyle={{
                        borderRadius: '12px',
                        border: 'none',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                        fontSize: '12px',
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Bar dataKey={t('aiUsage.text')} fill="#3b82f6" radius={[4, 4, 0, 0]} />
                    <Bar dataKey={t('aiUsage.image')} fill="#a855f7" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Alert Settings & History */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Alert Settings */}
          <Card data-testid="alert-settings-card">
            <CardContent className="p-5">
              <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                <Settings2 className="w-4 h-4 text-primary" />
                {t('aiUsage.alertSettings')}
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 rounded-xl border border-border/50 bg-muted/20">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Mail className="w-4.5 h-4.5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">{t('aiUsage.alertsEnabled')}</p>
                      <p className="text-xs text-muted-foreground">{t('aiUsage.alertsEnabledDesc')}</p>
                    </div>
                  </div>
                  <Switch
                    checked={alertsEnabled}
                    onCheckedChange={toggleAlerts}
                    disabled={savingSettings}
                    data-testid="alerts-toggle"
                  />
                </div>
                <div className="p-3 rounded-xl border border-border/50 bg-muted/20">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-9 h-9 rounded-lg bg-amber-500/10 flex items-center justify-center">
                      <Bell className="w-4.5 h-4.5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">{t('aiUsage.alertThreshold')}</p>
                      <p className="text-xs text-muted-foreground">80% & 100%</p>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <div className="flex-1 p-2 rounded-lg bg-amber-500/10 text-center">
                      <span className="text-xs font-bold text-amber-600">80%</span>
                      <p className="text-[10px] text-muted-foreground mt-0.5">{t('aiUsage.alertWarning')}</p>
                    </div>
                    <div className="flex-1 p-2 rounded-lg bg-red-500/10 text-center">
                      <span className="text-xs font-bold text-red-500">100%</span>
                      <p className="text-[10px] text-muted-foreground mt-0.5">{t('aiUsage.alertExceeded')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Alert History */}
          <Card data-testid="alert-history-card">
            <CardContent className="p-5">
              <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                <Clock className="w-4 h-4 text-primary" />
                {t('aiUsage.alertHistory')}
              </h3>
              {alerts.length === 0 ? (
                <div className="text-center py-8">
                  <Bell className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
                  <p className="text-xs text-muted-foreground">{t('aiUsage.noAlerts')}</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {alerts.map((alert, i) => (
                    <div
                      key={i}
                      className={`flex items-center gap-3 p-3 rounded-xl border ${
                        alert.alert_type === 'exceeded'
                          ? 'border-red-200 bg-red-50/50 dark:bg-red-950/20 dark:border-red-900'
                          : 'border-amber-200 bg-amber-50/50 dark:bg-amber-950/20 dark:border-amber-900'
                      }`}
                      data-testid={`alert-item-${i}`}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        alert.alert_type === 'exceeded' ? 'bg-red-500/10' : 'bg-amber-500/10'
                      }`}>
                        <AlertTriangle className={`w-4 h-4 ${
                          alert.alert_type === 'exceeded' ? 'text-red-500' : 'text-amber-500'
                        }`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <Badge className={`text-[9px] border-0 ${
                            alert.alert_type === 'exceeded'
                              ? 'bg-red-500/10 text-red-600'
                              : 'bg-amber-500/10 text-amber-600'
                          }`}>
                            {alert.alert_type === 'exceeded' ? t('aiUsage.alertExceeded') : t('aiUsage.alertWarning')}
                          </Badge>
                          <Badge className="text-[9px] bg-muted text-muted-foreground border-0">
                            {alert.type === 'text' ? t('aiUsage.textType') : t('aiUsage.imageType')}
                          </Badge>
                        </div>
                        <p className="text-[11px] text-muted-foreground mt-1">
                          {alert.current}/{alert.limit} ({alert.percentage}%)
                        </p>
                      </div>
                      <div className="text-[10px] text-muted-foreground flex-shrink-0">
                        {alert.timestamp?.split('T')[0]}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Plan Comparison */}
        <Card data-testid="plan-comparison">
          <CardContent className="p-5">
            <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
              <Crown className="w-4 h-4 text-primary" />
              {t('aiUsage.planComparison')}
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border/50">
                    <th className="text-start p-2.5 text-xs text-muted-foreground font-medium" />
                    {['trial', 'basic', 'pro', 'enterprise'].map(p => (
                      <th key={p} className="text-center p-2.5">
                        <span className={`text-xs font-bold ${p === plan ? 'text-primary' : 'text-foreground'}`}>
                          {p.toUpperCase()}
                        </span>
                        {p === plan && <Badge className="ms-1.5 text-[9px] bg-primary/10 text-primary border-0">الحالية</Badge>}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-border/30">
                    <td className="p-2.5 text-xs text-muted-foreground">{t('aiUsage.textRequests')}</td>
                    {[10, 100, 500, -1].map((v, i) => (
                      <td key={i} className="text-center p-2.5 text-xs font-semibold">
                        {v === -1 ? t('aiUsage.unlimited') : `${v}${t('aiUsage.perMonth')}`}
                      </td>
                    ))}
                  </tr>
                  <tr className="border-b border-border/30">
                    <td className="p-2.5 text-xs text-muted-foreground">{t('aiUsage.imageRequests')}</td>
                    {[3, 20, 100, -1].map((v, i) => (
                      <td key={i} className="text-center p-2.5 text-xs font-semibold">
                        {v === -1 ? t('aiUsage.unlimited') : `${v}${t('aiUsage.perMonth')}`}
                      </td>
                    ))}
                  </tr>
                  {ALL_FEATURES.map(feature => {
                    const featurePlans = {
                      chat: [true, true, true, true],
                      translation: [true, true, true, true],
                      description: [false, true, true, true],
                      image_generation: [false, false, true, true],
                      marketing: [false, false, true, true],
                      analytics_narration: [false, false, true, true],
                      order_agent: [false, false, false, true],
                    };
                    const available = featurePlans[feature] || [false, false, false, true];
                    return (
                      <tr key={feature} className="border-b border-border/30">
                        <td className="p-2.5 text-xs text-muted-foreground">{t(`aiUsage.${feature}`)}</td>
                        {available.map((v, i) => (
                          <td key={i} className="text-center p-2.5">
                            {v
                              ? <Check className="w-4 h-4 text-emerald-500 mx-auto" />
                              : <XIcon className="w-4 h-4 text-muted-foreground/40 mx-auto" />
                            }
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {plan !== 'enterprise' && (
              <div className="text-center mt-4">
                <Button
                  onClick={() => navigate('/dashboard/settings/subscription')}
                  className="rounded-xl"
                  data-testid="upgrade-plan-btn"
                >
                  <ArrowUpRight className="w-4 h-4 me-1.5" />
                  {t('aiUsage.upgradeNow')}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </ModuleLayout>
  );
}
